import { Button, Input, List, Modal, ScrollArea, Tooltip } from '@mantine/core'
import { IconCommand, IconSearch } from '@tabler/icons'
import React, { memo, useEffect, useRef } from 'react'
import useSearchNode from '../../hooks/useSearchNode';

const SearchNode = ({reactFlowInstance}) => {
  const inputRef = useRef(null);
  const [opened, setOpened] = React.useState(false);
  const { onSelectNode, selectedIndex, selected, handleSearch, results, handleKeyDown } = useSearchNode(reactFlowInstance);
  const listRef = useRef(null);

  useEffect(() => {
    if (opened) {
      selected.current.opened = true;
      handleSearch('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      selected.current.opened = false;
    }
  }, [opened, inputRef, handleSearch]);

  useEffect(() => {
    if (!reactFlowInstance) {
      return;
    }
    const handler = (e) => {
      const isF = typeof e.key === 'string' && e.key.toLowerCase() === 'f';
      const isCtrlOrMeta = e.ctrlKey || e.metaKey;
      if (isCtrlOrMeta && isF) {
        e.preventDefault();
        setOpened(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [reactFlowInstance]);

  const getDescNode = node => {
    if (node.data?.options) {
      if (['nodeStart', 'nodeVariables'].includes(node.data.type)) {
        return '';
      }
      const desc = Object.keys(node.data.options).map(key => {
        if (['string', 'number'].includes(typeof node.data.options[key]) && !!node.data.options[key]) {
          return `${key}: ${node.data.options[key]}`;
        }
      });
      return desc.filter(i => i).join(', ');
    }
    return '';
  }

  useEffect(() => {
    if (!listRef.current) {
      return;
    };
    const options = listRef.current.querySelectorAll('li');
    const option = options[selectedIndex];
    if (option) {
      option.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedIndex])

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 16,
          zIndex: 10,
        }}
      >
        <Tooltip
          label={<p style={{ alignItems: 'center', display: 'flex', fontSize: 16 }}>Ctrl+F / <IconCommand/> + F</p>}
          withArrow
        >
          <Button
            variant='light'
            size='sm'
            onClick={() => setOpened(true)}
          >
            <IconSearch/>
          </Button>
        </Tooltip>
      </div>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
        zIndex={999}
      >
        <Input
          icon={<IconSearch/>}
          style={{ width:'58vw' }}
          ref={inputRef}
          onChange={e => handleSearch(e.target.value)}
          onKeyDown={e => {
            e.stopPropagation();
            handleKeyDown(e);
            if (e.key === 'Enter' && selected.current.opened) {
              onSelectNode(selected.current.results[selected.current.index]);
              setOpened(false);
            }
          }}
        />

        <ScrollArea style={{ marginTop: 16, padding: 4, maxHeight: '80vh' }}>
          <List
            spacing='xs'
            size="md"
            center
            listStyleType='none'
            style={{ maxWidth: '60vw', maxHeight: '78vh' }}
            ref={listRef}
          >
            {results.map((node, idx) => (
              <List.Item
                key={node.id}
                onClick={() => {
                  setOpened(false);
                  onSelectNode(node);
                }}
                style={{
                  padding: 8,
                  cursor: 'pointer',
                  backgroundColor: selectedIndex === idx ? 'rgba(168, 168, 255, 0.39)' : 'transparent',
                  width: '100%',
                  borderRadius: 4
                }}
              >
                <div >
                  <p style={{ fontWeight: 'bold' }}>{node.data.label}</p>
                  <p
                    style={{
                      fontStyle: 'italic',
                      overflow:  'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineBreak: 'anywhere',
                      width: '58vw',
                      lineHeight: '1.2em',
                    }}
                  >{getDescNode(node)}</p>
                  {
                    node.data.comment && (
                      <p
                        style={{
                          fontStyle: 'italic',
                          overflow:  'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineBreak: 'anywhere',
                          width: '58vw',
                          lineHeight: '1.2em',
                        }}
                      >{node.data.comment}</p>
                    )
                  }
                </div>
              </List.Item>
            ))}
          </List>
        </ScrollArea>
      </Modal>
    </>
  )
}

export default memo(SearchNode);
